import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { useDispatch } from 'react-redux';
import { QrScanner } from '../../components/QrScanner';
import { CommonDatagrid } from '../../components/DataGrid';
import { RefreshButton } from '../../components/Buttons/RefreshButton';
import {
  getAllReturnsColumns,
  getExpectedReturnsColumns,
  getFailedConfirmationsColumns,
  getShortReturnsColumns,
} from '../../components/DataGrid/ReturnReception/columns';
import { ReturnReceptionService } from '../../api/services/returnReceptionService';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { showAlertDialog } from '../../store/Reducers/AlertDialogSlice';
import { IOrderReturnSearchParams } from '../../types/api/request/returnreceptionrequest';
import { IOrderReturn } from '../../types/api/response/returnreceptionresponse';
import { ReturnDetailDialog, ReturnDetailSource } from './ReturnDetailDialog';
import { ResolveReturnDialog } from './ResolveReturnDialog';
import {
  AllReturnsFilters,
  AllReturnsFilterValue,
  EMPTY_ALL_RETURNS_FILTER,
  toAllReturnsSearchParams,
} from './AllReturnsFilters';

const SCANNER_ELEMENT_ID = 'return-reception-reader';
const OVERDUE_ROW_CLASS = 'return-overdue';

type ListFilter = Omit<IOrderReturnSearchParams, 'search' | 'page' | 'size'>;

interface ReturnListTabProps {
  filter: ListFilter;
  columns: GridColDef[];
  refreshKey: number;
  /** Kept in the URL by the page, so it survives a reload. */
  search: string;
  onSearchChange: (search: string) => void;
  onRefresh: () => void;
  onRowClick: (orderReturn: IOrderReturn) => void;
  highlightOverdue?: boolean;
}

const ReturnListTab = ({
  filter,
  columns,
  refreshKey,
  search,
  onSearchChange,
  onRefresh,
  onRowClick,
  highlightOverdue = false,
}: ReturnListTabProps) => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState<IOrderReturn[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  // The filter object is rebuilt on every render; its serialised value is the real dependency.
  const filterKey = JSON.stringify(filter);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ReturnReceptionService.list({
        ...filter,
        search: search || undefined,
        page,
        size: pageSize,
      });
      setRows(res.content ?? []);
      setTotalElements(res.page?.totalElements ?? 0);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to load returns.',
          severity: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filterKey, search, page, pageSize]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <Box
      sx={{
        // Outranks CommonDatagrid's zebra-stripe row backgrounds; alpha() keeps it readable in
        // both light and dark themes.
        [`& .MuiDataGrid-root .MuiDataGrid-row.${OVERDUE_ROW_CLASS}`]: {
          backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.18),
          '&:hover': {
            backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.28),
          },
        },
      }}
    >
      <CommonDatagrid
        data={rows}
        columns={columns}
        rowId="return_ref"
        serverPagination
        page={page}
        pageSize={pageSize}
        rowCount={totalElements}
        loading={loading}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(0);
        }}
        initialSearch={search}
        onChangeSearchKeyword={(q) => {
          // Also fires on mount with the restored keyword — only a real change resets the page.
          if (q !== search) {
            onSearchChange(q);
            setPage(0);
          }
        }}
        searchbarPlaceholder="Search by order number or return ref..."
        headerActionButtons={<RefreshButton onClick={onRefresh} />}
        onRowClick={(params: GridRowParams) => onRowClick(params.row as IOrderReturn)}
        getRowClassName={
          highlightOverdue
            ? (params) => ((params.row as IOrderReturn).overdue ? OVERDUE_ROW_CLASS : '')
            : undefined
        }
      />
    </Box>
  );
};

const EXPECTED_FILTER: ListFilter = { status: 'EXPECTED' };
const SHORT_FILTER: ListFilter = { status: 'SHORT' };
const FAILED_FILTER: ListFilter = { confirmation_state: 'FAILED' };

const TABS = ['expected', 'shortfalls', 'failures', 'all'] as const;
type TabKey = (typeof TABS)[number];

// The work queues get a count on their tab; "All returns" is history, so it doesn't.
const COUNTED_TABS: [TabKey, ListFilter][] = [
  ['expected', EXPECTED_FILTER],
  ['shortfalls', SHORT_FILTER],
  ['failures', FAILED_FILTER],
];

// URL query keys. The page keeps its tab, search and "All returns" filters in the URL, so a
// reload or a shared link lands on the same view.
const PARAM_TAB = 'tab';
const PARAM_SEARCH = 'q';
const FILTER_PARAMS: Record<keyof AllReturnsFilterValue, string> = {
  status: 'status',
  source: 'source',
  confirmation: 'confirmation',
  receivedFrom: 'from',
  receivedTo: 'to',
};

const readAllReturnsFilter = (params: URLSearchParams): AllReturnsFilterValue =>
  Object.fromEntries(
    (Object.keys(FILTER_PARAMS) as (keyof AllReturnsFilterValue)[]).map((key) => [
      key,
      params.get(FILTER_PARAMS[key]) ?? EMPTY_ALL_RETURNS_FILTER[key],
    ]),
  ) as unknown as AllReturnsFilterValue;

const TabLabel = ({ label, count, alert }: { label: string; count?: number; alert?: boolean }) => (
  <Box component="span" display="inline-flex" alignItems="center" gap={1}>
    {label}
    {count !== undefined && (
      <Chip
        label={count}
        size="small"
        color={alert && count > 0 ? 'error' : 'default'}
        sx={{ height: 20, fontSize: 12 }}
      />
    )}
  </Box>
);

/** The open dialog's source with `updated` swapped in, if it shows that same return. */
const withUpdatedReturn = (
  source: ReturnDetailSource | null,
  updated: IOrderReturn,
): ReturnDetailSource | null => {
  if (source?.kind === 'return' && source.orderReturn.return_ref === updated.return_ref) {
    return { kind: 'return', orderReturn: updated };
  }
  if (source?.kind === 'lookup' && source.lookup.order_return?.return_ref === updated.return_ref) {
    return { kind: 'lookup', lookup: { ...source.lookup, order_return: updated } };
  }
  return source;
};

export const ReturnReceptionPage = () => {
  const dispatch = useDispatch();

  // Scan section
  const [manualMode, setManualMode] = useState(false);
  const [restartKey, setRestartKey] = useState(0);
  const [code, setCode] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [lastWarning, setLastWarning] = useState<string | null>(null);

  // Dialogs
  const [detailSource, setDetailSource] = useState<ReturnDetailSource | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [resolveTarget, setResolveTarget] = useState<IOrderReturn | null>(null);
  const [isResolveOpen, setIsResolveOpen] = useState(false);

  // Lists — tab, search and filters live in the URL
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get(PARAM_TAB) as TabKey | null;
  const tab: TabKey = tabParam && TABS.includes(tabParam) ? tabParam : 'expected';
  const search = searchParams.get(PARAM_SEARCH) ?? '';
  const allReturnsFilter = useMemo(() => readAllReturnsFilter(searchParams), [searchParams]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [counts, setCounts] = useState<Partial<Record<TabKey, number>>>({});
  const [now, setNow] = useState(() => Date.now());

  /** Sets (or, when empty, removes) URL params; replace keeps typing out of the back stack. */
  const updateParams = useCallback(
    (changes: Record<string, string>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(changes).forEach(([key, value]) =>
            value ? next.set(key, value) : next.delete(key),
          );
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // A new tab starts unfiltered — its own search and filters only.
  const changeTab = (next: TabKey) =>
    setSearchParams(next === 'expected' ? {} : { [PARAM_TAB]: next }, { replace: true });

  const changeSearch = useCallback(
    (q: string) => updateParams({ [PARAM_SEARCH]: q }),
    [updateParams],
  );

  const changeAllReturnsFilter = useCallback(
    (change: Partial<AllReturnsFilterValue>) =>
      updateParams(
        Object.fromEntries(
          Object.entries(change).map(([key, value]) => [
            FILTER_PARAMS[key as keyof AllReturnsFilterValue],
            value ?? '',
          ]),
        ),
      ),
    [updateParams],
  );

  const clearAllReturnsFilter = useCallback(
    () => changeAllReturnsFilter(EMPTY_ALL_RETURNS_FILTER),
    [changeAllReturnsFilter],
  );

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  const refreshLists = useCallback(() => setRefreshKey((k) => k + 1), []);
  const restartScanner = useCallback(() => setRestartKey((k) => k + 1), []);

  // Tab counts: a size-1 page of each queue, read for its totalElements. A failed count just
  // leaves the tab without a number — the list itself reports its own errors.
  useEffect(() => {
    let cancelled = false;
    Promise.allSettled(
      COUNTED_TABS.map(([key, filter]) =>
        ReturnReceptionService.list({ ...filter, page: 0, size: 1 }).then(
          (res) => [key, res.page?.totalElements ?? 0] as const,
        ),
      ),
    ).then((results) => {
      if (cancelled) return;
      const next: Partial<Record<TabKey, number>> = {};
      results.forEach((r) => {
        if (r.status === 'fulfilled') next[r.value[0]] = r.value[1];
      });
      setCounts(next);
    });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const handleLookup = useCallback(
    async (rawCode: string) => {
      const value = rawCode.trim();
      if (!value) {
        dispatch(
          showNotification({ message: 'Enter a barcode or order number.', severity: 'error' }),
        );
        return;
      }
      setLookingUp(true);
      setLastWarning(null);
      try {
        const lookup = await ReturnReceptionService.lookup(value);
        setLastWarning(lookup.warning);
        setDetailSource({ kind: 'lookup', lookup });
        setIsDetailOpen(true);
        setCode('');
      } catch (e: any) {
        const status = e?.response?.status;
        dispatch(
          showNotification({
            message:
              status === 404
                ? 'No order matches this code'
                : (e?.response?.data?.message ?? 'Failed to look up the code.'),
            severity: 'error',
          }),
        );
        restartScanner();
      } finally {
        setLookingUp(false);
      }
    },
    [dispatch, restartScanner],
  );

  const handleCameraError = useCallback(
    (message: string) => {
      dispatch(showNotification({ message, severity: 'error' }));
      setManualMode(true);
    },
    [dispatch],
  );

  const openReturnByRef = useCallback(
    async (row: IOrderReturn) => {
      try {
        const orderReturn = await ReturnReceptionService.get(row.return_ref);
        setDetailSource({ kind: 'return', orderReturn });
        setIsDetailOpen(true);
      } catch (e: any) {
        dispatch(
          showNotification({
            message: e?.response?.data?.message ?? 'Failed to load the return.',
            severity: 'error',
          }),
        );
      }
    },
    [dispatch],
  );

  const openResolve = useCallback((orderReturn: IOrderReturn) => {
    setIsDetailOpen(false);
    setResolveTarget(orderReturn);
    setIsResolveOpen(true);
  }, []);

  const handleResend = useCallback(
    (orderReturn: IOrderReturn) => {
      dispatch(
        showAlertDialog({
          title: 'Resend confirmation',
          description: `Resend the SuperPro confirmation for ${orderReturn.return_ref} (${orderReturn.order_number})?`,
          confirmText: 'Resend',
          onConfirm: async () => {
            try {
              const result = await ReturnReceptionService.resendConfirmation(
                orderReturn.return_ref,
              );
              setDetailSource((prev) => withUpdatedReturn(prev, result));
              dispatch(
                showNotification({
                  message:
                    result.confirmation_state === 'SENT'
                      ? `Confirmation for ${result.return_ref} sent.`
                      : `Confirmation for ${result.return_ref} re-queued (${result.confirmation_state ?? 'pending'}).`,
                  severity: result.confirmation_state === 'FAILED' ? 'warning' : 'success',
                }),
              );
              refreshLists();
            } catch (e: any) {
              dispatch(
                showNotification({
                  message: e?.response?.data?.message ?? 'Failed to resend confirmation.',
                  severity: 'error',
                }),
              );
            }
          },
        }),
      );
    },
    [dispatch, refreshLists],
  );

  const closeDetail = () => {
    setIsDetailOpen(false);
    restartScanner();
  };

  const expectedColumns = useMemo(() => getExpectedReturnsColumns(now), [now]);
  const shortColumns = useMemo(() => getShortReturnsColumns(openResolve), [openResolve]);
  const failedColumns = useMemo(() => getFailedConfirmationsColumns(handleResend), [handleResend]);
  const allColumns = useMemo(() => getAllReturnsColumns(), []);
  const allReturnsParams = useMemo(
    () => toAllReturnsSearchParams(allReturnsFilter),
    [allReturnsFilter],
  );

  const listProps = {
    refreshKey,
    search,
    onSearchChange: changeSearch,
    onRefresh: refreshLists,
    onRowClick: openReturnByRef,
  };

  const anyDialogOpen = isDetailOpen || isResolveOpen;

  return (
    <Box
      sx={{
        p: { xs: 0, md: 3 },
        // A single minmax(0, 1fr) track stops the wide data grids from stretching the page past
        // the viewport on a phone; each grid scrolls sideways inside its own box instead.
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: 3,
      }}
    >
      <Typography variant="h5" fontWeight={700}>
        Return Reception
      </Typography>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ maxWidth: 480, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Scan a package sticker or order number
          </Typography>

          {!manualMode ? (
            <>
              {/* Unmounted while a dialog is open so the camera is released and restarts on close. */}
              {!anyDialogOpen && !lookingUp && (
                <QrScanner
                  elementId={SCANNER_ELEMENT_ID}
                  onDecode={handleLookup}
                  onError={handleCameraError}
                  restartKey={restartKey}
                />
              )}
              {lookingUp && (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              )}
              <Button size="small" onClick={() => setManualMode(true)} disabled={lookingUp}>
                Enter code manually instead
              </Button>
            </>
          ) : (
            <>
              <Box
                component="form"
                sx={{ display: 'flex', gap: 1 }}
                onSubmit={(e) => {
                  e.preventDefault();
                  handleLookup(code);
                }}
              >
                <TextField
                  label="Package barcode or order number"
                  fullWidth
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  disabled={lookingUp}
                  autoFocus
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={lookingUp}
                  sx={{ flexShrink: 0 }}
                >
                  {lookingUp ? <CircularProgress size={24} /> : 'Look up'}
                </Button>
              </Box>
              <Button size="small" onClick={() => setManualMode(false)} disabled={lookingUp}>
                Use camera instead
              </Button>
            </>
          )}

          {lastWarning && (
            <Alert severity="warning" onClose={() => setLastWarning(null)}>
              {lastWarning}
            </Alert>
          )}
        </Box>
      </Paper>

      <Box>
        <Tabs
          value={tab}
          onChange={(_, value: TabKey) => changeTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 2 }}
        >
          <Tab
            value="expected"
            label={<TabLabel label="Expected from SuperPro" count={counts.expected} />}
          />
          <Tab
            value="shortfalls"
            label={<TabLabel label="Shortfalls" count={counts.shortfalls} alert />}
          />
          <Tab
            value="failures"
            label={<TabLabel label="Confirmation failures" count={counts.failures} alert />}
          />
          <Tab value="all" label="All returns" />
        </Tabs>

        {tab === 'expected' && (
          <ReturnListTab
            filter={EXPECTED_FILTER}
            columns={expectedColumns}
            highlightOverdue
            {...listProps}
          />
        )}
        {tab === 'shortfalls' && (
          <ReturnListTab filter={SHORT_FILTER} columns={shortColumns} {...listProps} />
        )}
        {tab === 'failures' && (
          <ReturnListTab filter={FAILED_FILTER} columns={failedColumns} {...listProps} />
        )}
        {tab === 'all' && (
          <>
            <AllReturnsFilters
              value={allReturnsFilter}
              onChange={changeAllReturnsFilter}
              onClear={clearAllReturnsFilter}
            />
            {/* Remounted per filter so paging starts again from the first page. */}
            <ReturnListTab
              key={JSON.stringify(allReturnsParams)}
              filter={allReturnsParams}
              columns={allColumns}
              {...listProps}
            />
          </>
        )}
      </Box>

      <ReturnDetailDialog
        source={detailSource}
        isOpen={isDetailOpen}
        onClose={closeDetail}
        onCompleted={refreshLists}
        onResolve={openResolve}
        onResend={handleResend}
      />

      <ResolveReturnDialog
        orderReturn={resolveTarget}
        isOpen={isResolveOpen}
        onClose={() => {
          setIsResolveOpen(false);
          restartScanner();
        }}
        onResolved={refreshLists}
      />
    </Box>
  );
};
